export const DOCUMENT_KINDS = [
  'pdf',
  'document',
  'spreadsheet',
  'presentation',
  'text',
  'image',
  'video',
  'audio',
  'other',
] as const;

export type DocumentKind = typeof DOCUMENT_KINDS[number];

/** `used` is the Recents tab. `recent` lists every file, as Ari needs. */
export type LibraryView = 'browse' | 'recent' | 'used' | 'bin';
export type LibrarySort = 'name' | 'created' | 'modified' | 'used';

export type ProcessingState = 'queued' | 'processing' | 'ready' | 'failed' | 'not_read';
export type NotReadReason =
  | 'unsupported'
  | 'allowance'
  | 'no_reader'
  | 'images_only'
  | 'too_large'
  | 'protected'
  | 'empty';

export interface LibraryEntry {
  type: 'folder' | 'document';
  id: string;
  name: string;
  parentId: string | null;
  kind: string | null;
  mimeType: string | null;
  byteSize: number | null;
  childCount: number;
  createdAt: string;
  modifiedAt: string;
  deletedAt: string | null;
  daysLeft: number | null;
  previewUrl: string | null;
  processingState: ProcessingState | null;
  notReadReason: NotReadReason | null;
  replacing: boolean;
  hasPreviewPdf: boolean;
  /** Width over height of the first page, when the cloud drew one. */
  pageAspect?: number | null;
  /** Pages in the file, when it has pages. */
  pageCount?: number | null;
  /** Pages that hold only images. Ari reads them only with OCR. */
  imagesOnlyPages?: number;
  /** The latest open, download or Ari citation. Only Recents has it. */
  usedAt?: string | null;
  /** The folders a binned item was in, top first. Empty at the top level. Only the bin has it. */
  location?: string[];
}

export interface LibraryResponse {
  items: LibraryEntry[];
  total: number;
  limit: number;
  offset: number;
  linksExpireInSeconds: number;
}

export interface FolderRecord {
  id: string;
  name: string;
  parentId: string | null;
  createdAt: string;
}

export interface DocumentsUsage {
  accountKind: 'user' | 'workspace';
  tier: string;
  usedBytes: number;
  limitBytes: number;
  processedBytes: number;
  processingLimitBytes: number;
  /** Support set this account's storage limit. */
  customLimit: boolean;
  /** This user's part of usedBytes. */
  ownUsedBytes?: number;
}

export interface FileLink {
  url: string;
  disposition: 'inline' | 'attachment';
  expiresInSeconds: number;
}

export type ItemOutcome =
  | 'deleted'
  | 'already_in_bin'
  | 'purging'
  | 'restored'
  | 'restored_to_top'
  | 'not_restorable'
  | 'not_found'
  | 'not_in_bin';

export interface ItemResult {
  type: string;
  id: string;
  outcome: ItemOutcome;
  /** A restore that found the name taken gives the item a new name. These say which, and where. */
  name?: string;
  previousName?: string;
  /** The folder the item went back to. null is the top level. */
  folderName?: string | null;
}

export interface OutcomesResponse {
  results: ItemResult[];
}

export type OpenUploadResponse =
  | {
      status: 'ready';
      sessionId: string;
      documentId: string;
      name: string;
      bucket: string;
      path: string;
      token: string;
    }
  | { status: 'already_complete'; documentId: string };

/** An upload that stopped: a closed tab, a crash or a lost network. It holds no room until resumed. */
export interface UnfinishedUpload {
  sessionId: string;
  clientUploadId: string;
  documentId: string;
  folderId: string | null;
  /** Set when the upload replaces this stored document. */
  replaceDocumentId: string | null;
  name: string;
  mimeType: string;
  byteSize: number;
  sentBytes: number;
  /** All bytes landed when this equals byteSize. Null while storage has no object. */
  storedBytes: number | null;
  checksumSha256: string;
  sampledSha256: string | null;
  resumeLocation: string | null;
  createdAt: string;
  /** The last heartbeat. Null when the upload was released before its first heartbeat. */
  lastSeenAt: string | null;
  expiresAt: string;
}

/** superseded: a newer version of the document was already there, so this one only went into its history. */
export type UploadOutcome = 'stored' | 'replacing' | 'unchanged' | 'superseded';

export interface CompleteUploadResponse {
  status: 'stored';
  documentId: string;
  name?: string;
  outcome?: UploadOutcome;
}

/** One hit from POST /documents/search. A name hit has no text. */
export interface DocumentSearchResult {
  documentId: string;
  /** With documentId, a source the viewer opens. Null for a name match. */
  chunkId: string | null;
  name: string;
  kind: string;
  folderPath: string;
  match: 'passage' | 'name';
  heading: string | null;
  text: string | null;
  place: string | null;
  score: number;
}

export interface DocumentSearchResponse {
  mode: 'hybrid' | 'text';
  folderFound: boolean | null;
  results: DocumentSearchResult[];
}

/** A source is a document and one passage of it, as a search found it. */
export interface DocumentSourceRef {
  documentId: string;
  chunkId: string;
  /** Words from the passage, as Ari cited them. Only their lines are marked. */
  quote?: string;
}

export type DocumentSourceState = 'live' | 'in_bin' | 'changed' | 'gone' | 'unread';

export interface DocumentSourceElement {
  type: string;
  text: string;
  page: number | null;
  locator: {
    page?: number;
    box?: [number, number, number, number];
    page_width?: number;
    page_height?: number;
    /** One [x0, y0, x1, y1, start, end] per line of written text. */
    line_boxes?: [number, number, number, number, number, number][];
    slide?: number;
    sheet?: string | null;
    range?: string;
    lines?: [number, number];
    start_ms?: number;
    end_ms?: number;
  };
}

/** From POST /documents/sources. Text and places come only with a live source. */
export interface DocumentSource extends DocumentSourceRef {
  state: DocumentSourceState;
  name: string | null;
  kind: string | null;
  folderPath: string | null;
  heading: string | null;
  place: string | null;
  elements: DocumentSourceElement[];
  pages: { page: number; url: string; width: number | null; height: number | null }[];
  originalUrl: string | null;
  /** The quote asked for was found, so only its lines came back. */
  quoted?: boolean;
}
